import { useDispatch } from 'react-redux';
import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Label, Button, Dropdown, Form, Icon, Modal } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import ButtonWrapper from '../common/ButtonWrapper';
import { inventoriesActions } from '../../behavior/inventories';
import { qrGenerationErrorHandler } from '../../objects/QRStickerGridPdfCreator';
import { Inventory } from '../../routes';
import { getCurrentLocationBaseUrl } from '../../utils';
import { defaultGridTemplateProvider } from '../../objects/StickerGridTemplate';
import { Inventories } from '../../behavior/inventories/sagas';
import { MOBILE } from '@src/constants';
import { DropdownProps } from 'semantic-ui-react/dist/commonjs/modules/Dropdown/Dropdown';

type Props = {
  selectedInventoriesDict: any;
  gridTemplateCreatorOptions: any;
  downloadFileName: string;
  isDropdownItem: boolean;
  classNamePopup: string;
};

const DownloadInventoryStickersModal = ({
  selectedInventoriesDict,
  gridTemplateCreatorOptions,
  downloadFileName,
  isDropdownItem,
  classNamePopup,
}: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [emptyStickers, setEmptyStickers] = useState(0);
  const [gridTemplateCreator, setGridTemplateCreator] = useState<() => any>(
    () => defaultGridTemplateProvider
  );

  const validatingOnClickHandler = () => {
    if (Object.keys(selectedInventoriesDict).length === 0) {
      toast.warn(t('message.title.warning.export.no.selected.rows'));
    } else {
      setOpenModal(true);
    }
  };

  const handleClose = () => {
    setEmptyStickers(0);
    setOpenModal(false);
  };

  const numericInputChangeHandler = (inputText: string) => {
    const numericValue = inputText.replace(/[^0-9]/, '');
    setEmptyStickers(Number(numericValue));
  };

  const inventoriesLabelsFetchingSupplier = () =>
    Inventories.getInventoriesChemicalProductsLabels(Object.keys(selectedInventoriesDict));
  const fetchingLabelsResultMapper = (res: { data: { id: string; label: string }[] }) =>
    Object.assign({}, ...res.data.map(e => ({ [e.id]: e.label })));

  return (
    <Modal
      onClose={() => {
        handleClose();
      }}
      open={openModal}
      trigger={downloadStickersTrigger(validatingOnClickHandler, isDropdownItem, classNamePopup)}
    >
      <Modal.Header>{t('download.stickers.modal.title')}</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Select
            label={t('download.stickers.modal.grid.selection')}
            options={gridTemplateCreatorOptions}
            value={gridTemplateCreator}
            onChange={(_ignored: any, { value }: DropdownProps) =>
              setGridTemplateCreator(() => value)
            }
          />
          <Form.Input
            label={t('download.stickers.modal.empty.number')}
            value={emptyStickers}
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
            onChange={(_ignored: any, { value }: DropdownProps) =>
              numericInputChangeHandler(value as string)
            }
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button
          color="red"
          onClick={() => {
            handleClose();
          }}
        >
          {t('cancel.button')}
        </Button>
        <Button
          positive
          disabled={isNaN(Number(emptyStickers))}
          onClick={() => {
            dispatch(
              inventoriesActions.createInventoriesQRStickersPdf(
                gridTemplateCreator(),
                Object.keys(selectedInventoriesDict),
                inventoriesLabelsFetchingSupplier,
                fetchingLabelsResultMapper,
                (id: string) =>
                  `${getCurrentLocationBaseUrl()}${Inventory.baseName}/${MOBILE}/${id}`,
                downloadFileName,
                qrGenerationErrorHandler(t)
              )
            );
            setOpenModal(false);
            toast.success(t('download.stickers.success.generating.stickers'));
          }}
        >
          {t('download.stickers.button.label')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const downloadStickersTrigger = (
  onClickHandler: () => void,
  isDropdownItem: boolean,
  classNamePopup: string
) => {
  const { t } = useTranslation();
  return isDropdownItem ? (
    <Dropdown.Item onClick={onClickHandler}>
      <Icon name="qrcode" color="blue" />
      <label className="blueTextLabel">{t('download.stickers.button.label')}</label>
    </Dropdown.Item>
  ) : (
    <ButtonWrapper
      label={t('download.stickers.button.label')}
      icon="qrcode"
      type="button"
      className={classNamePopup !== undefined ? `${classNamePopup} medium` : 'mini'}
      onClick={onClickHandler}
    />
  );
};

export default DownloadInventoryStickersModal;
